import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Shield, Heart, Brain, CheckCircle, Calendar, ArrowRight, Video, Activity, BookOpen, Download } from 'lucide-react';
import CTA from '../components/CTA';
import FreeGuideCTA from '../components/FreeGuideCTA';

const BOOK_WAITLIST_URL = 'https://sharonclare-hypnotherapy.kit.com/d06e0c0990';

const MedicalProcedureAnxiety: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Medical Procedure Anxiety | Feel Calm & In Control for Treatment</title>
        <meta name="description" content="When the nervous system learns fear, it can also learn safety. Hypnotherapy support for surgery, MRI scans, radiotherapy, blood tests and medical procedure anxiety." />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/medical-procedure-anxiety/" />
      </Helmet>

      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom text-center max-w-4xl mx-auto">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-4">Fear of Surgery or Medical Procedures</h1>
          <p className="text-xl font-heading text-gray-900 mb-4">
            Whether you’re facing surgery, radiotherapy, an MRI scan, blood tests or another medical procedure, anxiety can feel overwhelming. The good news is that what the nervous system learns, it can also learn differently.
          </p>
          <p className="text-base text-gray-700">
            Also specializing in <a href="/radiotherapy-anxiety" className="text-accent-600 hover:text-accent-700 underline">radiotherapy anxiety</a> and <a href="/anxiety-stress" className="text-accent-600 hover:text-accent-700 underline">general anxiety relief</a>.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/book" className="btn-primary inline-flex items-center justify-center">
              <Calendar className="w-5 h-5 mr-2" /> Request a 10-Minute Call
            </a>
            <a href="tel:07932139245" className="btn-secondary inline-flex items-center justify-center">
              <ArrowRight className="w-5 h-5 mr-2" /> Call 07932 139245
            </a>
          </div>
        </div>
      </section>

      {/* Why do I feel this way */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">Why Do I Feel This Way?</h2>
          <p className="text-gray-700 leading-relaxed mb-6">Many people tell me:</p>
          <blockquote className="border-l-4 border-accent-300 pl-4 italic text-gray-800 mb-6">
            “I know I need the treatment, but my body won’t cooperate.”
          </blockquote>
          <p className="text-gray-700 leading-relaxed mb-4">They understand logically that the procedure is necessary.</p>
          <ul className="text-gray-700 leading-relaxed mb-6 space-y-1">
            <li>Yet their heart races.</li>
            <li>Their breathing changes.</li>
            <li>They feel trapped.</li>
            <li>They become tearful.</li>
            <li>They avoid appointments.</li>
            <li>Or they spend weeks dreading what lies ahead.</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-4">
            This isn’t weakness. It isn’t a lack of willpower. Very often the nervous system has simply become highly practised at predicting danger and preparing for it automatically.
          </p>
          <p className="text-gray-700 leading-relaxed mb-2 font-semibold">The body has learned fear.</p>
          <p className="text-gray-700 leading-relaxed">The encouraging news is that the brain and body can also learn safety.</p>

          <div className="mt-8 bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex items-center space-x-3 mb-3">
              <Video className="w-6 h-6 text-accent-600" />
              <h3 className="font-heading font-semibold text-gray-900">Short explainer video</h3>
            </div>
            <div className="aspect-video rounded-lg overflow-hidden bg-gray-100">
              <video
                controls
                preload="metadata"
                playsInline
                className="w-full h-full"
                src="/videos/mri-claustrophobia.mp4"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </section>

      {/* Who I help */}
      <section className="py-16 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">Medical Procedures I Commonly Help With</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              'Surgery anxiety',
              'MRI and scan anxiety',
              'Radiotherapy anxiety',
              'Radiotherapy mask anxiety',
              'Needle phobia',
              'Blood test anxiety',
              'Chemotherapy anxiety',
              'Brachytherapy anxiety',
              'Claustrophobia in medical settings',
              'Hospital-related anxiety',
            ].map((item) => (
              <div key={item} className="flex items-center space-x-3 bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <CheckCircle className="w-5 h-5 text-accent-600 flex-shrink-0" />
                <span className="text-gray-800 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hospital experience */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">When Fear Gets in the Way of Treatment</h2>
          <div className="space-y-5 text-gray-700 leading-relaxed">
            <p>
              Alongside my private practice, I currently provide hypnotherapy support within Belfast City Hospital as part of a pilot programme developed in partnership with the Macmillan Information &amp; Support Centre.
            </p>
            <p>
              The pilot was created to support patients experiencing significant anxiety before cancer treatment, particularly those struggling with radiotherapy mask anxiety, claustrophobia, treatment-related fear, anticipatory nausea and overwhelming distress before appointments.
            </p>
            <p>
              What has become increasingly clear through this work is that many of these difficulties are not caused by a lack of information or understanding. Patients often know exactly why treatment is important. The challenge is that the nervous system has learned to respond as though the situation is dangerous, creating powerful fear responses that can feel impossible to control.
            </p>
            <p>
              My approach focuses on rapid nervous system regulation techniques designed to help people feel calmer, more confident and more in control. These include breathing strategies, safe place visualisation, mindfulness-based techniques and personalised calm anchors that can be used during treatment.
            </p>
            <p className="font-semibold text-gray-900">In many cases, meaningful change can happen surprisingly quickly.</p>
            <p>
              Recently, I worked with a patient who was extremely distressed about attending radiotherapy treatment later that afternoon. During a single session we focused on helping her nervous system settle and creating a practical anchor she could use throughout treatment. She attended her radiotherapy appointment confidently that same day.
            </p>
            <p>
              While every person’s experience is different, examples like this demonstrate an important principle: when fear is being driven by learned nervous system responses, helping the body feel safe can create rapid and meaningful change.
            </p>
            <p>
              Following the success of the pilot, discussions are underway to expand support into additional cancer services, including support for patients living with brain cancer and other treatment pathways where fear, distress and nervous system activation can become barriers to care.
            </p>
            <p>
              Whether the challenge is surgery, MRI scans, radiotherapy, blood tests, injections, cancer treatment or another medical procedure, my aim is simple: to help people feel calm enough to access the treatment they need and regain a sense of confidence and control.
            </p>
          </div>
        </div>
      </section>

      {/* How hypnotherapy helps (benefits) */}
      <section className="py-16 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">How Hypnotherapy Can Help</h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Clinical hypnotherapy works with the subconscious — the part of your mind responsible for automatic reactions. In a natural, focused state, you can learn to calm the alarm system and respond with clarity.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: <Shield className="w-6 h-6 text-accent-600" />, title: 'Switch off fight‑or‑flight' },
              { icon: <Heart className="w-6 h-6 text-accent-600" />, title: 'Feel calmer before/after appointments' },
              { icon: <Brain className="w-6 h-6 text-accent-600" />, title: 'Respond with logic and confidence' },
              { icon: <CheckCircle className="w-6 h-6 text-green-600" />, title: 'Sleep better and feel in control' },
            ].map(({ icon, title }) => (
              <div key={title} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-center space-x-3">
                  {icon}
                  <h3 className="font-heading font-semibold text-gray-900">{title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How I help (session detail) */}
      <section className="py-16 bg-white">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">How I Help</h2>
          <p className="text-gray-700 leading-relaxed mb-6">Sessions may include:</p>
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {[
              'Understanding how fear affects the brain and body',
              'Nervous system regulation techniques',
              'Breathing and grounding strategies',
              'Safe place visualisation',
              'Calm anchors',
              'Preparing mentally for procedures',
              'Building confidence and control',
            ].map((item) => (
              <div key={item} className="flex items-start space-x-3">
                <Activity className="w-5 h-5 text-accent-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-800">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-gray-700 leading-relaxed">
            The aim is to help your brain and body respond differently when the day arrives.
          </p>
        </div>
      </section>

      {/* What to expect */}
      <section className="py-16 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-6">What to Expect</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            You’ll start with an Initial Consultation (in person or online). We’ll explore what’s been happening for you, how your mind is responding, and what you’d like to change. From there, you’ll get a personalised plan — usually 4–6 sessions — to help you move forward feeling calm and capable.
          </p>
        </div>
      </section>

      {/* Book & Free Guide */}
      <FreeGuideCTA location="Medical Procedure Anxiety" />

      <section className="py-16 bg-white">
        <div className="container-custom max-w-4xl">
          <div className="bg-gradient-to-br from-primary-50 via-white to-accent-50 border-2 border-accent-200 rounded-3xl p-8 sm:p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-100 rounded-2xl mb-4">
              <BookOpen className="w-8 h-8 text-accent-600" />
            </div>
            <span className="inline-block bg-accent-600 text-white text-xs font-semibold px-3 py-1 rounded-full mb-3 uppercase tracking-wide">
              Coming Soon
            </span>
            <h2 className="text-2xl sm:text-3xl font-heading font-bold text-gray-900 mb-4">
              A New Book on the Fear of Surgery
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6 max-w-2xl mx-auto">
              I’m writing a book for anyone whose fear gets in the way of the treatment they need. Join the waiting list to be the first to hear when it’s available.
            </p>
            <a
              href={BOOK_WAITLIST_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center justify-center"
            >
              <Download className="w-5 h-5 mr-2" />
              Join the Book Waiting List
            </a>
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section className="py-16 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-gray-900 mb-6">
              Medical Anxiety Support Near You
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Get help with surgery fears, MRI anxiety, and medical procedures at our Belfast or Lisburn clinics, or online:
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <a href="/belfast-hypnotherapy" className="bg-white rounded-xl p-6 hover:shadow-lg transition-shadow border-2 border-accent-200">
                <h3 className="font-semibold text-gray-900 mb-2">Belfast</h3>
                <p className="text-sm text-gray-600 mb-1">Days &amp; times vary — early evening available</p>
                <p className="text-xs text-gray-500">Near Belfast City Hospital</p>
              </a>
              <a href="/lisburn-hypnotherapy" className="bg-white rounded-xl p-6 hover:shadow-lg transition-shadow border-2 border-accent-200">
                <h3 className="font-semibold text-gray-900 mb-2">Lisburn</h3>
                <p className="text-sm text-gray-600 mb-1">Times vary — early evening available</p>
                <p className="text-xs text-gray-500">The Arzen Collective, Antrim Street</p>
              </a>
              <a href="/contact" className="bg-white rounded-xl p-6 hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-gray-900 mb-2">Online</h3>
                <p className="text-sm text-gray-600 mb-1">Flexible Scheduling</p>
                <p className="text-xs text-gray-500">Across Northern Ireland & UK</p>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTA
        title="Ready to Feel Calm Again?"
        subtitle="Book your initial consultation and take the first step toward calmer, more confident appointments and procedures."
        primaryButtonText="Request a 10-Minute Call"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default MedicalProcedureAnxiety;
